import { CSSProperties, RefObject, useCallback, useEffect, useState } from 'react';

import { useMutationObserver } from '@libs/hooks/useMutationObserver';

type Options = {
  minFontSize?: number;
  maxFontSize?: number;
  maxHeight?: number;
};

const DEFAULT_OPTIONS = {
  minFontSize: 11,
  maxFontSize: 120,
};

export function useDynamicFontScaling(
  ref: RefObject<HTMLDivElement | null>,
  options: Options = DEFAULT_OPTIONS,
): CSSProperties {
  const [fontSize, setFontSize] = useState<number | undefined>(undefined);

  const recalculateScale = useCallback(() => {
    if (!ref.current || !ref.current.parentElement) return;
    const textWidth = ref.current.clientWidth;
    const textHeight = ref.current.clientHeight;
    const containerWidth = ref.current.parentElement.clientWidth;

    const currentFontSize = Number(window.getComputedStyle(ref.current).fontSize.slice(0, -2));

    let targetFontSize = currentFontSize;
    if (textWidth > containerWidth) {
      const currentScale = textWidth / currentFontSize;
      targetFontSize = containerWidth / currentScale;
    }

    if (options.maxHeight && textHeight > options.maxHeight) {
      const currentScaleHeight = textHeight / currentFontSize;
      targetFontSize = Math.min(options.maxHeight / currentScaleHeight, targetFontSize);
    }

    setFontSize(
      Math.min(
        options.maxFontSize || DEFAULT_OPTIONS.maxFontSize,
        Math.max(options.minFontSize || DEFAULT_OPTIONS.minFontSize, targetFontSize),
      ),
    );
  }, [ref, setFontSize, options]);

  useMutationObserver(ref, () => {
    recalculateScale();
  });

  useEffect(() => {
    if (!ref.current) return;
    recalculateScale();
  });

  return {
    fontSize,
  };
}
